/** Statistics, placement and hoop checks computed from the actual stitch sequence. */
import { fmtInt, t } from '../../i18n/i18n';
import { type BBox, emptyBBox } from '../geometry/polygon';
import type { Vec } from '../geometry/vec';
import type { Issue } from '../model/issues';
import type { MachineSettings } from '../model/types';
import { CMD, type StitchSequence } from './sequence';

export interface DesignStats {
  /** Needle penetrations (STITCH commands). */
  stitches: number;
  /** Jump sequences (consecutive JUMP commands count as one). */
  jumps: number;
  trims: number;
  colorChanges: number;
  stops: number;
  /** Colour blocks (threads in sewing order). */
  colorBlocks: number;
  /** Distinct thread colours. */
  uniqueColors: number;
  /** Bounding box of all penetrations, mm. */
  bbox: BBox;
  widthMm: number;
  heightMm: number;
  /** Total length of sewn thread on top (excl. jumps), metres. */
  threadLengthM: number;
  /** Rough sewing time estimate in seconds. */
  estimatedSeconds: number;
  /** Sewing order: object indices in order of first appearance. */
  objectOrder: number[];
}

export function stitchBBox(seq: StitchSequence): BBox {
  const b = emptyBBox();
  for (let i = 0; i < seq.count; i++) {
    if (seq.cmds[i] !== CMD.STITCH) continue;
    const x = seq.xs[i], y = seq.ys[i];
    if (x < b.minX) b.minX = x;
    if (y < b.minY) b.minY = y;
    if (x > b.maxX) b.maxX = x;
    if (y > b.maxY) b.maxY = y;
  }
  return b;
}

export function computeStats(seq: StitchSequence, machine: MachineSettings): DesignStats {
  let stitches = 0, jumps = 0, trims = 0, colorChanges = 0, stops = 0;
  let thread = 0;
  let prevCmd = -1;
  let px = 0, py = 0;
  let hasPrev = false;
  const order: number[] = [];
  const seen = new Set<number>();
  for (let i = 0; i < seq.count; i++) {
    const c = seq.cmds[i];
    const x = seq.xs[i], y = seq.ys[i];
    switch (c) {
      case CMD.STITCH:
        stitches++;
        if (hasPrev && prevCmd === CMD.STITCH) thread += Math.hypot(x - px, y - py);
        break;
      case CMD.JUMP:
        if (prevCmd !== CMD.JUMP) jumps++;
        break;
      case CMD.TRIM:
        trims++;
        break;
      case CMD.COLOR_CHANGE:
        colorChanges++;
        break;
      case CMD.STOP:
        stops++;
        break;
    }
    const o = seq.objs[i];
    if (o >= 0 && !seen.has(o)) {
      seen.add(o);
      order.push(o);
    }
    prevCmd = c;
    px = x;
    py = y;
    hasPrev = true;
  }
  const bbox = stitchBBox(seq);
  const valid = bbox.maxX >= bbox.minX;
  const estimatedSeconds =
    (stitches / machine.speedSpm) * 60 + colorChanges * machine.colorChangeSeconds + trims * machine.trimSeconds + jumps * 0.2;
  return {
    stitches,
    jumps,
    trims,
    colorChanges,
    stops,
    colorBlocks: seq.threads.length,
    uniqueColors: new Set(seq.threads.map((t) => t.hex.toLowerCase())).size,
    bbox,
    widthMm: valid ? bbox.maxX - bbox.minX : 0,
    heightMm: valid ? bbox.maxY - bbox.minY : 0,
    threadLengthM: thread / 1000,
    estimatedSeconds,
    objectOrder: order,
  };
}

/** The origin used for export: the hoop centre (manual placement) or the stitch bbox centre. */
export function exportOrigin(seq: StitchSequence, machine: MachineSettings): Vec {
  if (machine.hoopCenter) return machine.hoopCenter;
  const b = stitchBBox(seq);
  if (!(b.maxX >= b.minX)) return { x: 0, y: 0 };
  return { x: (b.minX + b.maxX) / 2, y: (b.minY + b.maxY) / 2 };
}

/** Checks that every penetration and jump target lies within the hoop. */
export function checkHoop(seq: StitchSequence, machine: MachineSettings): Issue[] {
  const issues: Issue[] = [];
  if (!seq.count) return issues;
  const o = exportOrigin(seq, machine);
  const hw = machine.hoop.widthMm / 2;
  const hh = machine.hoop.heightMm / 2;
  const b = stitchBBox(seq);
  if (!(b.maxX >= b.minX)) return issues;
  const w = b.maxX - b.minX;
  const h = b.maxY - b.minY;
  if (w > machine.hoop.widthMm + 1e-6 || h > machine.hoop.heightMm + 1e-6) {
    issues.push({
      severity: 'warning',
      code: 'hoop-size',
      message: t('The design ({w} × {h} mm) is larger than the hoop {hoop}. Scale the design down or choose a larger hoop.', { w: w.toFixed(1), h: h.toFixed(1), hoop: t(machine.hoop.name) }),
    });
  } else if (b.minX < o.x - hw - 1e-6 || b.maxX > o.x + hw + 1e-6 || b.minY < o.y - hh - 1e-6 || b.maxY > o.y + hh + 1e-6) {
    issues.push({
      severity: 'warning',
      code: 'hoop-placement',
      message: t('Parts of the design are outside the hoop with the chosen placement. Centre the design in the hoop.'),
    });
  }
  return issues;
}

export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h) return t('{h} h {m} min', { h, m });
  if (m) return t('{m} min {s} s', { m, s: r });
  return t('{s} s', { s: r });
}

/** Above these counts files become impractical (machine memory, sewing time). */
export const STITCH_WARN = 250_000;
export const STITCH_MAX = 1_000_000;

export function checkComplexity(stats: DesignStats): Issue[] {
  if (stats.stitches > STITCH_MAX) {
    return [{ severity: 'error', code: 'too-many-stitches', message: t('The design has {n} stitches (max {max}). Make the design smaller or increase the row spacing.', { n: fmtInt(stats.stitches), max: fmtInt(STITCH_MAX) }) }];
  }
  if (stats.stitches > STITCH_WARN) {
    return [{ severity: 'warning', code: 'many-stitches', message: t('The design has a very large number of stitches ({n}). Many machines limit the stitch count; consider a lower density.', { n: fmtInt(stats.stitches) }) }];
  }
  return [];
}
